<template>
    <div
        :class="[
            hasError(formErrors, 'card_number') ||
            cardTokenizationErrors.card_number ||
            cardTokenizationErrors.expiration ||
            hasError(formErrors, 'expiration') ||
            hasErrorStartingWith(formErrors, 'billing')
                ? 'border-red-600'
                : 'border-gray-200',
            'mt-4 mb-6 md:mt-2 lg:mb-4 bg-white shadow rounded-lg border',
        ]"
    >
        <div
            :class="[
                hasError(formErrors, 'card_number') ||
                cardTokenizationErrors.card_number ||
                cardTokenizationErrors.expiration ||
                hasError(formErrors, 'expiration') ||
                hasErrorStartingWith(formErrors, 'billing')
                    ? 'bg-red-500 text-white border-red-600'
                    : 'bg-gray-100 text-gray-800 border-gray-200',
                'flex items-center justify-between px-4 py-5 sm:px-6 rounded-t-lg border-b',
            ]"
        >
            <div class="flex items-center">
                <CreditCardIcon class="h-5 w-5 mr-2" aria-hidden="true" />
                <h2 class="font-bold">Payment Method</h2>
            </div>
        </div>

        <div class="md:grid md:grid-cols-2 md:gap-6 px-4 py-5 sm:px-6">
            <div class="mt-0 md:col-span-2">
                <RadioGroup v-model="payWith">
                    <RadioGroupLabel />
                    <div class="bg-white rounded-md -space-y-px">
                        <RadioGroupOption
                            data-test="pay-with-card"
                            as="template"
                            value="card"
                            v-slot="{ checked }"
                        >
                            <div
                                :class="[
                                    checked
                                        ? 'bg-emerald-50 border-nutreance-green-lighter z-10'
                                        : 'border-gray-300',
                                    'relative border p-4 flex cursor-pointer focus:outline-none rounded-tl-md rounded-tr-md',
                                ]"
                            >
                                <span
                                    :class="[
                                        checked
                                            ? 'bg-nutreance-green border-transparent'
                                            : 'bg-white border-gray-300',
                                        'h-4 w-4 mt-0.5 cursor-pointer rounded-full border flex items-center justify-center shrink-0',
                                    ]"
                                    aria-hidden="true"
                                >
                                    <span
                                        class="rounded-full bg-white w-1.5 h-1.5"
                                    />
                                </span>

                                <div class="ml-3 flex flex-col">
                                    <RadioGroupLabel
                                        as="span"
                                        :class="[
                                            checked
                                                ? 'text-emerald-700'
                                                : 'text-gray-500',
                                            'block text-sm sm:inline-flex',
                                        ]"
                                    >
                                        <span class="mr-2"
                                            >Credit/Debit Card</span
                                        >
                                        <div class="flex mt-1 sm:mt-0">
                                            <div class="mx-1">
                                                <svg-vue
                                                    class="w-8 sm:w-10 rounded shadow"
                                                    :icon="'payments/visa'"
                                                    aria-hidden="true"
                                                />
                                            </div>
                                            <div class="mx-1">
                                                <svg-vue
                                                    class="w-8 sm:w-10 rounded shadow"
                                                    :icon="'payments/mastercard'"
                                                    aria-hidden="true"
                                                />
                                            </div>
                                            <div class="mx-1">
                                                <svg-vue
                                                    class="w-8 sm:w-10 rounded shadow"
                                                    :icon="'payments/american-express'"
                                                    aria-hidden="true"
                                                />
                                            </div>
                                            <div class="mx-1">
                                                <svg-vue
                                                    class="w-8 sm:w-10 rounded shadow"
                                                    :icon="'payments/discover'"
                                                    aria-hidden="true"
                                                />
                                            </div>
                                        </div>
                                    </RadioGroupLabel>
                                </div>
                            </div>
                        </RadioGroupOption>
                        <RadioGroupOption
                            data-test="pay-with-apple-pay"
                            @click="onPayWithApplePay"
                            v-if="applePayAvailable"
                            as="template"
                            value="apple_pay"
                            v-slot="{ checked }"
                        >
                            <div
                                :class="[
                                    checked
                                        ? 'bg-emerald-50 border-nutreance-green-lighter z-10'
                                        : 'border-gray-300',
                                    'relative border p-4 flex cursor-pointer focus:outline-none',
                                ]"
                            >
                                <span
                                    :class="[
                                        checked
                                            ? 'bg-nutreance-green border-transparent'
                                            : 'bg-white border-gray-300',
                                        'h-4 w-4 mt-0.5 cursor-pointer rounded-full border flex items-center justify-center shrink-0',
                                    ]"
                                    aria-hidden="true"
                                >
                                    <span
                                        class="rounded-full bg-white w-1.5 h-1.5"
                                    />
                                </span>

                                <div class="ml-3 flex flex-col">
                                    <RadioGroupLabel
                                        as="span"
                                        :class="[
                                            checked
                                                ? 'text-emerald-700'
                                                : 'text-gray-500',
                                            'text-sm block',
                                        ]"
                                    >
                                        <span class="inline align-middle"
                                            >Pay with</span
                                        >
                                        <svg-vue
                                            class="inline ml-0.5 -my-4 h-8 shrink-0"
                                            :icon="'payments/apple-pay'"
                                            aria-hidden="true"
                                        />
                                    </RadioGroupLabel>
                                </div>
                            </div>
                        </RadioGroupOption>
                        <RadioGroupOption
                            data-test="pay-with-paypal"
                            @click="onPayWithPaypal"
                            as="template"
                            value="paypal"
                            :disabled="errors.paypal_token || paypalError"
                            v-slot="{ checked }"
                        >
                            <div
                                :class="[
                                    checked
                                        ? 'bg-emerald-50 border-nutreance-green-lighter z-10'
                                        : 'border-gray-300',
                                    'relative border p-4 flex cursor-pointer focus:outline-none rounded-bl-md rounded-br-md',
                                ]"
                            >
                                <span
                                    :class="[
                                        checked
                                            ? 'bg-nutreance-green border-transparent'
                                            : 'bg-white border-gray-300',
                                        'h-4 w-4 mt-0.5 cursor-pointer rounded-full border flex items-center justify-center shrink-0',
                                    ]"
                                    aria-hidden="true"
                                >
                                    <span
                                        class="rounded-full bg-white w-1.5 h-1.5"
                                    />
                                </span>

                                <div class="ml-3 flex flex-col">
                                    <RadioGroupLabel
                                        as="span"
                                        :class="[
                                            checked
                                                ? 'text-emerald-700'
                                                : 'text-gray-500',
                                            'text-sm block',
                                        ]"
                                    >
                                        <span
                                            v-if="
                                                !errors.paypal_token &&
                                                !paypalError
                                            "
                                            class="inline align-middle"
                                            >Pay with</span
                                        >
                                        <svg-vue
                                            class="inline -my-4 h-12 shrink-0"
                                            :icon="'payments/paypal'"
                                            aria-hidden="true"
                                        />
                                        <span
                                            v-if="
                                                errors.paypal_token &&
                                                !paypalError
                                            "
                                            class="inline -ml-0.5 align-middle"
                                            >is unavailable</span
                                        >
                                        <span
                                            v-else-if="
                                                !errors.paypal_token &&
                                                paypalError
                                            "
                                            class="inline -ml-0.5 align-middle text-red-600"
                                        >
                                            was blocked by your pop-up blocker.
                                            Please allow pop-ups to use this
                                            option.</span
                                        >
                                    </RadioGroupLabel>
                                </div>
                            </div>
                        </RadioGroupOption>
                    </div>
                </RadioGroup>

                <input
                    type="hidden"
                    name="data_descriptor"
                    :value="inputs.dataDescriptor"
                />
                <input
                    type="hidden"
                    name="data_value"
                    :value="inputs.dataValue"
                />

                <div v-if="payWith === 'card'">
                    <div class="mt-6 grid grid-cols-6 gap-6">
                        <div
                            class="order-1 col-span-6 sm:col-span-4 md:col-span-3"
                        >
                            <label
                                for="card_number"
                                class="block text-sm font-medium text-gray-700"
                            >
                                Card Number
                            </label>
                            <div class="mt-1 relative rounded-md shadow-sm">
                                <cleave
                                    @blur="onBlur"
                                    v-model="inputs.card_number"
                                    :options="{
                                        creditCard: true,
                                        delimiter: '  ',
                                    }"
                                    :ref="refs.card_number"
                                    id="card_number"
                                    :name="cardNumberInputName"
                                    type="text"
                                    inputmode="numeric"
                                    autocomplete="cc-number"
                                    required
                                    :class="[
                                        hasError(formErrors, 'card_number') ||
                                        cardTokenizationErrors.card_number
                                            ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500 pr-10'
                                            : 'focus:ring-nutreance-green focus:border-nutreance-green',
                                        'appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none sm:text-sm',
                                    ]"
                                    :aria-invalid="
                                        hasError(formErrors, 'card_number') ||
                                        cardTokenizationErrors.card_number
                                            ? 'true'
                                            : 'false'
                                    "
                                    :aria-describedby="
                                        hasError(formErrors, 'card_number') ||
                                        cardTokenizationErrors.card_number
                                            ? 'card_number-error'
                                            : ''
                                    "
                                />
                                <div
                                    v-if="cardType"
                                    class="absolute inset-y-0 right-0 pr-1 flex items-center pointer-events-none"
                                >
                                    <svg-vue
                                        :icon="svgPathForCardType(cardType)"
                                        :key="cardType"
                                        class="w-10 h-auto rounded shadow"
                                        aria-hidden="true"
                                    />
                                </div>
                            </div>
                            <p
                                v-if="
                                    hasError(formErrors, 'card_number') ||
                                    cardTokenizationErrors.card_number
                                "
                                class="mt-2 text-sm text-red-600"
                                id="card_number-error"
                            >
                                {{
                                    hasError(formErrors, "card_number")
                                        ? error(formErrors, "card_number")
                                        : cardTokenizationErrors.card_number
                                }}
                            </p>
                        </div>

                        <div
                            class="order-3 sm:order-2 flex items-end -ml-2 sm:ml-0 col-span-2 md:col-span-3"
                        >
                            <a
                                href="https://www.trustedsite.com/verify?host=nutreance.com"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <img
                                    :src="asset('/images/trustedsite.svg')"
                                    alt="TrustedSite Certified Secure"
                                />
                            </a>
                        </div>

                        <div
                            class="order-2 sm:order-3 col-span-4 sm:col-span-3 md:col-span-2"
                        >
                            <label
                                for="card_number"
                                class="block text-sm font-medium text-gray-700"
                            >
                                Expiration (MM/YY)
                            </label>
                            <div class="mt-1 relative rounded-md shadow-sm">
                                <cleave
                                    @blur="onBlur"
                                    v-model="inputs.expiration"
                                    :options="{
                                        date: true,
                                        datePattern: ['m', 'y'],
                                    }"
                                    :ref="refs.expiration"
                                    id="expiration"
                                    name="expiration"
                                    type="text"
                                    inputmode="numeric"
                                    autocomplete="cc-exp"
                                    required
                                    :class="[
                                        hasError(formErrors, 'expiration') ||
                                        cardTokenizationErrors.expiration
                                            ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500 pr-10'
                                            : 'focus:ring-nutreance-green focus:border-nutreance-green',
                                        'appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none sm:text-sm',
                                    ]"
                                    :aria-invalid="
                                        hasError(formErrors, 'expiration') ||
                                        cardTokenizationErrors.expiration
                                            ? 'true'
                                            : 'false'
                                    "
                                    :aria-describedby="
                                        hasError(formErrors, 'expiration') ||
                                        cardTokenizationErrors.expiration
                                            ? 'expiration-error'
                                            : ''
                                    "
                                />
                                <div
                                    v-if="
                                        hasError(formErrors, 'expiration') ||
                                        cardTokenizationErrors.expiration
                                    "
                                    class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                                >
                                    <ExclamationCircleIcon
                                        class="h-5 w-5 text-red-500"
                                        aria-hidden="true"
                                    />
                                </div>
                            </div>
                            <p
                                v-if="
                                    hasError(formErrors, 'expiration') ||
                                    cardTokenizationErrors.expiration
                                "
                                class="mt-2 text-sm text-red-600"
                                id="expiration-error"
                            >
                                {{
                                    hasError(formErrors, "expiration")
                                        ? error(formErrors, "expiration")
                                        : cardTokenizationErrors.expiration
                                }}
                            </p>
                        </div>
                    </div>

                    <div class="mt-6">
                        <input
                            type="hidden"
                            name="billing_same_as_shipping"
                            :value="inputs.billing_same_as_shipping ? '1' : ''"
                        />

                        <RadioGroup v-model="inputs.billing_same_as_shipping">
                            <RadioGroupLabel
                                class="font-semibold text-gray-500"
                            >
                                Billing Address:
                            </RadioGroupLabel>

                            <div
                                class="mt-4 md:mt-2 bg-white rounded-md -space-y-px"
                            >
                                <RadioGroupOption
                                    data-test="billing-same-as-shipping"
                                    as="template"
                                    :value="true"
                                    v-slot="{ checked }"
                                >
                                    <div
                                        :class="[
                                            checked
                                                ? 'bg-emerald-50 border-nutreance-green-lighter z-10'
                                                : 'border-gray-300',
                                            'relative border p-4 flex cursor-pointer focus:outline-none rounded-tl-md rounded-tr-md',
                                        ]"
                                    >
                                        <span
                                            :class="[
                                                checked
                                                    ? 'bg-nutreance-green border-transparent'
                                                    : 'bg-white border-gray-300',
                                                'h-4 w-4 mt-0.5 cursor-pointer rounded-full border flex items-center justify-center shrink-0',
                                            ]"
                                            aria-hidden="true"
                                        >
                                            <span
                                                class="rounded-full bg-white w-1.5 h-1.5"
                                            />
                                        </span>

                                        <div class="ml-3 flex flex-col">
                                            <RadioGroupLabel
                                                as="span"
                                                :class="[
                                                    checked
                                                        ? 'text-emerald-700'
                                                        : 'text-gray-500',
                                                    'block text-sm',
                                                ]"
                                            >
                                                Same as shipping address
                                            </RadioGroupLabel>
                                        </div>
                                    </div>
                                </RadioGroupOption>
                                <RadioGroupOption
                                    data-test="billing-different-from-shipping"
                                    as="template"
                                    :value="false"
                                    v-slot="{ checked }"
                                >
                                    <div
                                        :class="[
                                            checked
                                                ? 'bg-emerald-50 border-nutreance-green-lighter z-10'
                                                : 'border-gray-300',
                                            'relative border p-4 flex cursor-pointer focus:outline-none rounded-bl-md rounded-br-md',
                                        ]"
                                    >
                                        <span
                                            :class="[
                                                checked
                                                    ? 'bg-nutreance-green border-transparent'
                                                    : 'bg-white border-gray-300',
                                                'h-4 w-4 mt-0.5 cursor-pointer rounded-full border flex items-center justify-center shrink-0',
                                            ]"
                                            aria-hidden="true"
                                        >
                                            <span
                                                class="rounded-full bg-white w-1.5 h-1.5"
                                            />
                                        </span>

                                        <div class="ml-3 flex flex-col">
                                            <RadioGroupLabel
                                                as="span"
                                                :class="[
                                                    checked
                                                        ? 'text-emerald-700'
                                                        : 'text-gray-500',
                                                    'block text-sm',
                                                ]"
                                            >
                                                Use a different billing address
                                            </RadioGroupLabel>
                                        </div>
                                    </div>
                                </RadioGroupOption>
                            </div>
                        </RadioGroup>

                        <div
                            v-if="!inputs.billing_same_as_shipping"
                            class="mt-6 grid grid-cols-6 gap-6"
                        >
                            <div class="col-span-6">
                                <button
                                    v-if="!inputs.billing_same_as_shipping"
                                    @click="copyShippingAddressToBillingAddress"
                                    name="copy_shipping_to_billing"
                                    type="button"
                                    class="flex items-center justify-end text-sm text-nutreance-green hover:text-nutreance-green-lighter focus:outline-none"
                                >
                                    <DuplicateIcon
                                        class="h-5 w-5 mr-2 shrink-0"
                                    />
                                    Copy From Shipping Address
                                </button>
                            </div>

                            <div class="col-span-6 sm:col-span-4">
                                <label
                                    for="billing_name"
                                    class="block text-sm font-medium text-gray-700"
                                >
                                    Full Name
                                </label>
                                <div class="mt-1 relative rounded-md shadow-sm">
                                    <input
                                        v-model="inputs.name"
                                        :ref="refs.name"
                                        id="billing_name"
                                        name="billing_name"
                                        type="text"
                                        autocomplete="billing name"
                                        required
                                        :class="[
                                            hasError(formErrors, 'billing_name')
                                                ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500 pr-10'
                                                : 'focus:ring-nutreance-green focus:border-nutreance-green',
                                            'appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none sm:text-sm',
                                        ]"
                                        :aria-invalid="
                                            hasError(formErrors, 'billing_name')
                                                ? 'true'
                                                : 'false'
                                        "
                                        :aria-describedby="
                                            hasError(formErrors, 'billing_name')
                                                ? 'billing_name-error'
                                                : ''
                                        "
                                    />
                                    <div
                                        v-if="
                                            hasError(formErrors, 'billing_name')
                                        "
                                        class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                                    >
                                        <ExclamationCircleIcon
                                            class="h-5 w-5 text-red-500"
                                            aria-hidden="true"
                                        />
                                    </div>
                                </div>
                                <p
                                    v-if="hasError(formErrors, 'billing_name')"
                                    class="mt-2 text-sm text-red-600"
                                    id="billing_name-error"
                                >
                                    {{ error(formErrors, "billing_name") }}
                                </p>
                            </div>

                            <div class="col-span-6">
                                <label
                                    for="billing_line1"
                                    class="block text-sm font-medium text-gray-700"
                                >
                                    {{ ucwords(country.line1) }}
                                </label>
                                <div class="mt-1 relative rounded-md shadow-sm">
                                    <input
                                        v-model="inputs.line1"
                                        :ref="refs.line1"
                                        @placeChanged="onPlaceChanged"
                                        @keydown.enter="onEnterKeyDown"
                                        @change="onAddressChanged"
                                        name="billing_line1"
                                        id="billing_line1"
                                        type="text"
                                        required
                                        placeholder=""
                                        autocomplete="billing address-line1"
                                        :class="[
                                            hasError(
                                                formErrors,
                                                'billing_line1',
                                            )
                                                ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500 pr-10'
                                                : 'focus:ring-nutreance-green focus:border-nutreance-green',
                                            verificationErrors.line1
                                                ? 'pr-10'
                                                : '',
                                            'appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none sm:text-sm',
                                        ]"
                                        :aria-invalid="
                                            hasError(
                                                formErrors,
                                                'billing_line1',
                                            )
                                                ? 'true'
                                                : 'false'
                                        "
                                        :aria-describedby="
                                            hasError(
                                                formErrors,
                                                'billing_line1',
                                            )
                                                ? 'billing_line1-error'
                                                : ''
                                        "
                                    />
                                    <div
                                        v-if="
                                            hasError(
                                                formErrors,
                                                'billing_line1',
                                            ) && !verificationErrors.line1
                                        "
                                        class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                                    >
                                        <ExclamationCircleIcon
                                            class="h-5 w-5 text-red-500"
                                            aria-hidden="true"
                                        />
                                    </div>
                                    <div
                                        v-if="verificationErrors.line1"
                                        class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                                    >
                                        <QuestionMarkCircleIcon
                                            class="h-5 w-5 text-amber-500"
                                            aria-hidden="true"
                                        />
                                    </div>
                                </div>
                                <p
                                    v-if="hasError(formErrors, 'billing_line1')"
                                    class="mt-2 text-sm text-red-600"
                                    id="billing_line1-error"
                                >
                                    {{ error(formErrors, "billing_line1") }}
                                </p>
                                <p
                                    v-if="verificationErrors.line1"
                                    class="mt-2 text-sm text-amber-600"
                                    id="billing_line1-verification-error"
                                >
                                    {{ verificationErrors.line1 }}
                                </p>
                            </div>

                            <div class="col-span-6">
                                <label
                                    for="billing_line2"
                                    class="block text-sm font-medium text-gray-700"
                                >
                                    {{ ucwords(country.line2) }}
                                </label>

                                <div class="mt-1 relative rounded-md shadow-sm">
                                    <input
                                        v-model="inputs.line2"
                                        :ref="refs.line2"
                                        @change="onAddressChanged"
                                        name="billing_line2"
                                        id="billing_line2"
                                        type="text"
                                        autocomplete="billing address-line2"
                                        placeholder="(optional)"
                                        :class="[
                                            hasError(
                                                formErrors,
                                                'billing_line2',
                                            )
                                                ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500 pr-10'
                                                : 'focus:ring-nutreance-green focus:border-nutreance-green',
                                            verificationErrors.line2
                                                ? 'pr-10'
                                                : '',
                                            'appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none sm:text-sm',
                                        ]"
                                        :aria-invalid="
                                            hasError(
                                                formErrors,
                                                'billing_line2',
                                            )
                                                ? 'true'
                                                : 'false'
                                        "
                                        :aria-describedby="
                                            hasError(
                                                formErrors,
                                                'billing_line2',
                                            )
                                                ? 'billing_line2-error'
                                                : ''
                                        "
                                    />
                                    <div
                                        v-if="
                                            hasError(
                                                formErrors,
                                                'billing_line2',
                                            ) && !verificationErrors.line2
                                        "
                                        class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                                    >
                                        <ExclamationCircleIcon
                                            class="h-5 w-5 text-red-500"
                                            aria-hidden="true"
                                        />
                                    </div>
                                    <div
                                        v-if="verificationErrors.line2"
                                        class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                                    >
                                        <QuestionMarkCircleIcon
                                            class="h-5 w-5 text-amber-500"
                                            aria-hidden="true"
                                        />
                                    </div>
                                </div>
                                <p
                                    v-if="hasError(formErrors, 'billing_line2')"
                                    class="mt-2 text-sm text-red-600"
                                    id="billing_line2-error"
                                >
                                    {{ error(formErrors, "billing_line2") }}
                                </p>
                                <p
                                    v-if="verificationErrors.line2"
                                    class="mt-2 text-sm text-amber-600"
                                    id="billing_line2-verification-error"
                                >
                                    {{ verificationErrors.line2 }}
                                </p>
                            </div>

                            <div
                                v-if="country.city"
                                class="col-span-6 sm:col-span-6 lg:col-span-3"
                            >
                                <label
                                    for="billing_city"
                                    class="block text-sm font-medium text-gray-700"
                                >
                                    {{ ucwords(country.city) }}
                                </label>

                                <div class="mt-1 relative rounded-md shadow-sm">
                                    <input
                                        v-model="inputs.city"
                                        :ref="refs.city"
                                        @change="onAddressChanged"
                                        name="billing_city"
                                        id="billing_city"
                                        type="text"
                                        autocomplete="billing address-level2"
                                        required
                                        :class="[
                                            hasError(formErrors, 'billing_city')
                                                ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500 pr-10'
                                                : 'focus:ring-nutreance-green focus:border-nutreance-green',
                                            'appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none sm:text-sm',
                                        ]"
                                        :aria-invalid="
                                            hasError(formErrors, 'billing_city')
                                                ? 'true'
                                                : 'false'
                                        "
                                        :aria-describedby="
                                            hasError(formErrors, 'billing_city')
                                                ? 'billing_city-error'
                                                : ''
                                        "
                                    />
                                    <div
                                        v-if="
                                            hasError(formErrors, 'billing_city')
                                        "
                                        class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                                    >
                                        <ExclamationCircleIcon
                                            class="h-5 w-5 text-red-500"
                                            aria-hidden="true"
                                        />
                                    </div>
                                </div>
                                <p
                                    v-if="hasError(formErrors, 'billing_city')"
                                    class="mt-2 text-sm text-red-600"
                                    id="billing_city-error"
                                >
                                    {{ error(formErrors, "billing_city") }}
                                </p>
                            </div>

                            <div
                                v-if="country.state"
                                class="col-span-6 sm:col-span-3"
                            >
                                <label
                                    for="billing_state"
                                    class="block text-sm font-medium text-gray-700"
                                >
                                    {{ ucwords(country.state) }}
                                </label>

                                <div class="mt-1 relative rounded-md shadow-sm">
                                    <input
                                        v-if="isEmpty(states)"
                                        v-model="inputs.state"
                                        :ref="refs.state"
                                        @change="onAddressChanged"
                                        name="billing_state"
                                        id="billing_state"
                                        type="text"
                                        autocomplete="billing address-level1"
                                        required
                                        :class="[
                                            hasError(
                                                formErrors,
                                                'billing_state',
                                            )
                                                ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500 pr-10'
                                                : 'focus:ring-nutreance-green focus:border-nutreance-green',
                                            'appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none sm:text-sm',
                                        ]"
                                        :aria-invalid="
                                            hasError(
                                                formErrors,
                                                'billing_state',
                                            )
                                                ? 'true'
                                                : 'false'
                                        "
                                        :aria-describedby="
                                            hasError(
                                                formErrors,
                                                'billing_state',
                                            )
                                                ? 'billing_state-error'
                                                : ''
                                        "
                                    />
                                    <select
                                        v-else
                                        v-model="inputs.state"
                                        :ref="refs.state"
                                        @change="onAddressChanged"
                                        id="billing_state"
                                        name="billing_state"
                                        autocomplete="billing address-level1"
                                        required
                                        :class="[
                                            hasError(
                                                formErrors,
                                                'billing_state',
                                            )
                                                ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500 pr-14'
                                                : 'focus:ring-nutreance-green focus:border-nutreance-green',
                                            'mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none sm:text-sm',
                                        ]"
                                        :aria-invalid="
                                            hasError(
                                                formErrors,
                                                'billing_state',
                                            )
                                                ? 'true'
                                                : 'false'
                                        "
                                        :aria-describedby="
                                            hasError(
                                                formErrors,
                                                'billing_state',
                                            )
                                                ? 'billing_state-error'
                                                : ''
                                        "
                                    >
                                        <option
                                            value=""
                                            disabled
                                            hidden
                                            selected
                                        >
                                            --- Select a
                                            {{ ucwords(country.state) }} ---
                                        </option>
                                        <option
                                            v-for="(state, code) in states"
                                            :key="code"
                                            :value="code"
                                        >
                                            {{ state }}
                                        </option>
                                    </select>
                                    <div
                                        v-if="
                                            hasError(
                                                formErrors,
                                                'billing_state',
                                            )
                                        "
                                        :class="[
                                            isEmpty(states) ? 'pr-3' : 'pr-8',
                                            'absolute inset-y-0 right-0 flex items-center pointer-events-none',
                                        ]"
                                    >
                                        <ExclamationCircleIcon
                                            class="h-5 w-5 text-red-500"
                                            aria-hidden="true"
                                        />
                                    </div>
                                </div>
                                <p
                                    v-if="hasError(formErrors, 'billing_state')"
                                    class="mt-2 text-sm text-red-600"
                                    id="billing_state-error"
                                >
                                    {{ error(formErrors, "billing_state") }}
                                </p>
                            </div>

                            <div
                                v-if="country.postal_code"
                                class="col-span-6 sm:col-span-3 lg:col-span-2"
                            >
                                <label
                                    for="billing_postal_code"
                                    class="block text-sm font-medium text-gray-700"
                                >
                                    {{ ucwords(country.postal_code) }}
                                </label>

                                <div class="mt-1 relative rounded-md shadow-sm">
                                    <input
                                        v-model="inputs.postal_code"
                                        :ref="refs.postal_code"
                                        @change="onAddressChanged"
                                        name="billing_postal_code"
                                        id="billing_postal_code"
                                        type="text"
                                        autocomplete="billing postal-code"
                                        :required="
                                            country.shipping_region_field ===
                                            'postal_code'
                                        "
                                        :class="[
                                            hasError(
                                                formErrors,
                                                'billing_postal_code',
                                            )
                                                ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500 pr-10'
                                                : 'focus:ring-nutreance-green focus:border-nutreance-green',
                                            verificationErrors.postal_code
                                                ? 'pr-10'
                                                : '',
                                            'appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none sm:text-sm',
                                        ]"
                                        :aria-invalid="
                                            hasError(
                                                formErrors,
                                                'billing_postal_code',
                                            )
                                                ? 'true'
                                                : 'false'
                                        "
                                        :aria-describedby="
                                            hasError(
                                                formErrors,
                                                'billing_postal_code',
                                            )
                                                ? 'billing_postal_code-error'
                                                : ''
                                        "
                                    />
                                    <div
                                        v-if="
                                            hasError(
                                                formErrors,
                                                'billing_postal_code',
                                            ) && !verificationErrors.postal_code
                                        "
                                        class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                                    >
                                        <ExclamationCircleIcon
                                            class="h-5 w-5 text-red-500"
                                            aria-hidden="true"
                                        />
                                    </div>
                                    <div
                                        v-if="verificationErrors.postal_code"
                                        class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                                    >
                                        <QuestionMarkCircleIcon
                                            class="h-5 w-5 text-amber-500"
                                            aria-hidden="true"
                                        />
                                    </div>
                                </div>
                                <p
                                    v-if="
                                        hasError(
                                            formErrors,
                                            'billing_postal_code',
                                        )
                                    "
                                    class="mt-2 text-sm text-red-600"
                                    id="billing_postal_code-error"
                                >
                                    {{
                                        error(formErrors, "billing_postal_code")
                                    }}
                                </p>
                                <p
                                    v-if="verificationErrors.postal_code"
                                    class="mt-2 text-sm text-amber-600"
                                    id="billing_postal_code-verification-error"
                                >
                                    {{ verificationErrors.postal_code }}
                                </p>
                            </div>

                            <div class="col-span-6 sm:col-span-4">
                                <label
                                    for="billing_country_code"
                                    class="block text-sm font-medium text-gray-700"
                                >
                                    Country
                                </label>

                                <div class="mt-1 relative rounded-md shadow-sm">
                                    <select
                                        v-model="inputs.country_code"
                                        :ref="refs.country_code"
                                        @change="updateCountry"
                                        id="billing_country_code"
                                        name="billing_country_code"
                                        autocomplete="billing country"
                                        required
                                        :class="[
                                            hasError(
                                                formErrors,
                                                'billing_country_code',
                                            )
                                                ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500 pr-14'
                                                : 'focus:ring-nutreance-green focus:border-nutreance-green',
                                            'mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none sm:text-sm',
                                        ]"
                                        :aria-invalid="
                                            hasError(
                                                formErrors,
                                                'billing_country_code',
                                            )
                                                ? 'true'
                                                : 'false'
                                        "
                                        :aria-describedby="
                                            hasError(
                                                formErrors,
                                                'billing_country_code',
                                            )
                                                ? 'billing_country_code-error'
                                                : ''
                                        "
                                    >
                                        <option
                                            v-for="(country, code) in countries"
                                            :key="code"
                                            :value="code"
                                        >
                                            {{ country }}
                                        </option>
                                    </select>
                                    <div
                                        v-if="
                                            hasError(
                                                formErrors,
                                                'billing_country_code',
                                            )
                                        "
                                        class="absolute inset-y-0 right-0 pr-8 flex items-center pointer-events-none"
                                    >
                                        <ExclamationCircleIcon
                                            class="h-5 w-5 text-red-500"
                                            aria-hidden="true"
                                        />
                                    </div>
                                </div>
                                <p
                                    v-if="
                                        hasError(
                                            formErrors,
                                            'billing_country_code',
                                        )
                                    "
                                    class="mt-2 text-sm text-red-600"
                                    id="billing_country_code-error"
                                >
                                    {{
                                        error(
                                            formErrors,
                                            "billing_country_code",
                                        )
                                    }}
                                </p>
                            </div>

                            <div class="col-span-6 sm:col-span-3">
                                <label
                                    for="billing_phone"
                                    class="block text-sm font-medium text-gray-700"
                                >
                                    Phone
                                </label>

                                <div class="mt-1 relative rounded-md shadow-sm">
                                    <input
                                        v-model="inputs.phone"
                                        :ref="refs.phone"
                                        @focus="onAddressChanged"
                                        name="billing_phone"
                                        id="billing_phone"
                                        type="tel"
                                        autocomplete="phone"
                                        placeholder="(optional)"
                                        :class="[
                                            hasError(
                                                formErrors,
                                                'billing_phone',
                                            )
                                                ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500'
                                                : 'focus:ring-nutreance-green focus:border-nutreance-green',
                                            'appearance-none block w-full pl-3 pr-10 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none sm:text-sm',
                                        ]"
                                        :aria-invalid="
                                            hasError(
                                                formErrors,
                                                'billing_phone',
                                            )
                                                ? 'true'
                                                : 'false'
                                        "
                                        :aria-describedby="
                                            hasError(
                                                formErrors,
                                                'billing_phone',
                                            )
                                                ? 'billing_phone-error'
                                                : ''
                                        "
                                    />
                                    <div
                                        v-if="
                                            hasError(
                                                formErrors,
                                                'billing_phone',
                                            )
                                        "
                                        class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                                    >
                                        <ExclamationCircleIcon
                                            class="h-5 w-5 text-red-500"
                                            aria-hidden="true"
                                        />
                                    </div>
                                    <InputTooltip
                                        v-else
                                        message="In case we need to contact you about your order"
                                    />
                                </div>
                                <p
                                    v-if="hasError(formErrors, 'billing_phone')"
                                    class="mt-2 text-sm text-red-600"
                                    id="billing_phone-error"
                                >
                                    {{ error(formErrors, "billing_phone") }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-show="payWith === 'paypal'" class="mt-4">
                    <PaypalCheckoutButton
                        :amount="amount"
                        :errors="errors"
                        :token="paypalToken"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import {
    computed,
    inject,
    nextTick,
    onMounted,
    reactive,
    ref,
    watch,
} from "vue";
import type { Ref } from "vue";
import { DuplicateIcon } from "@heroicons/vue/outline";
import {
    CreditCardIcon,
    ExclamationCircleIcon,
    QuestionMarkCircleIcon,
} from "@heroicons/vue/solid";
import { RadioGroup, RadioGroupLabel, RadioGroupOption } from "@headlessui/vue";
import { useForm } from "../../functions/useForm";
import Cleave from "vue-cleave-component";
import { useStaticAsset } from "../../functions/useStaticAsset";
import { usePaypal } from "../../functions/usePaypal";
import { usePaymentMethod } from "../../functions/usePaymentMethod";
import { useGoogleMapsAutocomplete } from "../../functions/useGoogleMapsAutocomplete";
import { useString } from "../../functions/useString";
import { useVerifyAddress } from "../../functions/useVerifyAddress";
import { useApplePay } from "../../functions/useApplePay";
import { useAcceptJs } from "../../functions/useAcceptJs";
import { isEmpty } from "lodash";
import InputTooltip from "../InputTooltip.vue";
import PaypalCheckoutButton from "../cart/PaypalCheckoutButton.vue";
import { updateCountry as updateCountryRequest } from "../../api/countries";
import type { AppEmitter } from "../../emitter";

const props = defineProps<{
    amount?: number;
    countries?: any;
    errors?: any;
    formErrors?: any;
    initialCountry?: any;
    initialStates?: any;
    old?: any;
    paymentGateway?: any;
    paypalToken?: string;
}>();

const emit = defineEmits<{
    (e: "paymentMethodEntered"): void;
    (e: "cardDataTokenized"): void;
    (e: "cardDataTokenizationFailed"): void;
    (e: "payWithUpdated", value: string): void;
}>();

const emitter = inject<AppEmitter>("mitt")!;

const { error, hasError, hasErrorStartingWith, oldInput } = useForm();
const { cardTypeSlug } = usePaymentMethod();
const {
    isCardValidationError,
    loadAcceptJs,
    tokenizeCardData,
    tokenizationError,
} = useAcceptJs();
const {
    initializeAutocomplete,
    onEnterKeyDown,
    pasteAutocompletedAddress,
    restrictAutocompleteByCountry,
} = useGoogleMapsAutocomplete();
const { ucwords } = useString();
const { verificationErrors, verifyAddress } = useVerifyAddress();
const { applePayAvailable, createPaymentRequest } = useApplePay();
const { asset } = useStaticAsset();
const { paypalError } = usePaypal();

const payWith = ref("card");
const cardType = computed(() => cardTypeSlug(inputs.card_number));
const paymentMethodEntered = ref(false);
const cardNumberInputName = ref<string | null>(null);

const country = ref(props.initialCountry);
const states = ref(props.initialStates);
const oldCountryCode = oldInput(props.old, "billing_country_code");

if (oldCountryCode && oldCountryCode !== country.value.code) {
    updateCountry(oldCountryCode);
}

const refs: Record<string, Ref<any>> = {
    card_number: ref(null),
    expiration: ref(null),
    name: ref(null),
    line1: ref(null),
    line2: ref(null),
    city: ref(null),
    state: ref(null),
    postal_code: ref(null),
    country_code: ref(null),
    phone: ref(null),
};

const inputs = reactive<Record<string, any>>({
    card_number: "",
    expiration: "",
    dataDescriptor: "",
    dataValue: "",
    name: oldInput(props.old, "billing_name"),
    line1: oldInput(props.old, "billing_line1"),
    line2: oldInput(props.old, "billing_line2"),
    city: oldInput(props.old, "billing_city"),
    state: oldInput(props.old, "billing_state")
        ? oldInput(props.old, "billing_state")
        : "",
    postal_code: oldInput(props.old, "billing_postal_code"),
    country_code: country.value.code,
    phone: oldInput(props.old, "billing_phone"),
    billing_same_as_shipping: !!(Object.prototype.hasOwnProperty.call(
        props.old,
        "billing_same_as_shipping",
    )
        ? oldInput(props.old, "billing_same_as_shipping")
        : true),
});

const cardTokenizationErrors = reactive<Record<string, any>>({
    card_number: "",
    expiration: "",
});

if (
    window.location.protocol === "https:" &&
    typeof ApplePaySession !== "undefined"
) {
    applePayAvailable.value = ApplePaySession.canMakePayments();
}

onMounted(() => {
    loadAcceptJs(!!props.paymentGateway.sandbox);

    if (!inputs.billing_same_as_shipping) {
        initializeAutocomplete(
            refs.line1.value,
            country.value.code,
            onPlaceChanged,
        );
    }

    emitter.on("copiedShippingAddressFields", (shippingInputs) =>
        pasteShippingAddressFields(shippingInputs),
    );
    emitter.on("applePayOpen", (isOpen) => {
        payWith.value = isOpen ? "apple_pay" : "card";
    });
    emitter.on("paypalPopupOpen", (isOpen) => {
        paypalError.value = false;
        payWith.value = isOpen ? "paypal" : "card";
    });
    emitter.on("paypalCheckoutError", () => {
        paypalError.value = true;
        payWith.value = "card";
    });
    emitter.on("tokenizeCardData", async () => {
        cardTokenizationErrors.card_number = null;
        cardTokenizationErrors.expiration = null;

        try {
            const result = await tokenizeCardData(
                props.paymentGateway,
                inputs.card_number,
                inputs.expiration,
            );

            if (result) {
                inputs.dataDescriptor = result.dataDescriptor;
                inputs.dataValue = result.dataValue;
            } else {
                cardNumberInputName.value = "card_number";
            }

            nextTick(() => emit("cardDataTokenized"));
        } catch (error: any) {
            if (!isCardValidationError(error)) {
                cardNumberInputName.value = "card_number";
                inputs.dataDescriptor = "";
                inputs.dataValue = "";
                nextTick(() => emit("cardDataTokenized"));
                return;
            }

            const { field, message } = tokenizationError(error);

            cardTokenizationErrors[field] = message;
            nextTick(() => refs[field].value?.$el?.focus());
            emit("cardDataTokenizationFailed");
        }
    });

    switch (true) {
        case !!hasError(props.formErrors, "card_number"):
            return refs.card_number.value.$el.focus();
        case !!hasError(props.formErrors, "expiration"):
            return refs.expiration.value.$el.focus();
        case !!hasError(props.formErrors, "billing_name"):
            return refs.name.value.focus();
        case !!hasError(props.formErrors, "billing_line1"):
            return refs.line1.value.focus();
        case !!hasError(props.formErrors, "billing_line2"):
            return refs.line2.value.focus();
        case !!hasError(props.formErrors, "billing_city"):
            return refs.city.value.focus();
        case !!hasError(props.formErrors, "billing_state"):
            return refs.state.value.focus();
        case !!hasError(props.formErrors, "billing_postal_code"):
            return refs.postal_code.value.focus();
        case !!hasError(props.formErrors, "billing_country_code"):
            return refs.country_code.value.focus();
        case !!hasError(props.formErrors, "billing_phone"):
            return refs.phone.value.focus();
    }
});

watch(
    () => inputs.billing_same_as_shipping,
    (billingSameAsShipping) => {
        if (!billingSameAsShipping) {
            nextTick(() => {
                refs.name.value.focus();
                initializeAutocomplete(
                    refs.line1.value,
                    country.value.code,
                    onPlaceChanged,
                );
            });
        }
    },
);

function copyShippingAddressToBillingAddress() {
    emitter.emit("copyShippingAddressToBillingAddress");
}

function onPayWithApplePay() {
    if (props.amount === undefined) {
        return;
    }

    createPaymentRequest(props.amount);
}

function onPayWithPaypal() {
    payWith.value = "paypal";
}

function onPlaceChanged() {
    refs.line1.value.value = inputs.line1;
    pasteAutocompletedAddress(inputs);
    onAddressChanged();
}

function pasteShippingAddressFields(shippingInputs: any) {
    for (const field in inputs) {
        if (
            Object.prototype.hasOwnProperty.call(inputs, field) &&
            Object.prototype.hasOwnProperty.call(shippingInputs, field)
        ) {
            inputs[field] = shippingInputs[field];
        }
    }

    updateCountry();
}

function svgPathForCardType(cardType: string) {
    return `payments/${cardType}`;
}

// `countryCode` is a string when called from script, but the `@change` binding in
// the template invokes this bare, passing the DOM Event; in that case we fall back
// to the current `inputs.country_code` (matching the prior untyped behavior).
function updateCountry(countryCode?: string | Event) {
    const code =
        typeof countryCode === "string" && countryCode.length === 2
            ? countryCode
            : inputs.country_code;

    updateCountryRequest({
        country_code: code,
    }).then((data) => {
        inputs.country_code = data.country.code;
        country.value = data.country;
        states.value = data.states;
        restrictAutocompleteByCountry(country.value.code);
    });
}

// Bound bare to `@change`/`@focus` in the template, so it receives a DOM Event that
// the body does not use.
function onAddressChanged() {
    verifyAddress(inputs);
}

watch(payWith, (value) => {
    emit("payWithUpdated", value);
});

function onBlur() {
    if (
        inputs.card_number &&
        inputs.expiration &&
        !paymentMethodEntered.value
    ) {
        emit("paymentMethodEntered");
        paymentMethodEntered.value = true;
    }
}
</script>
